/**
 * Reference list → "Group as variants of a parent": pick which selected
 * reference becomes the parent; the rest become its variants. Blocked, never
 * half-applied, when a conflict would nest variants. See services/variantGrouping.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { getVariantCountForParent, logAdminEvents } from '../../services/firebase';
import { applyGroupAsVariants, defaultGroupParent, planGroupAsVariants } from '../../services/variantGrouping';
import type { WatchBrand, WatchReference } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  brand: WatchBrand;
  selectedRefs: WatchReference[];
  onGrouped: () => void;
}

export function GroupAsVariantsDialog({ isOpen, onClose, brand, selectedRefs, onGrouped }: Props) {
  const [parentId, setParentId] = useState<string | undefined>(undefined);
  const [counts, setCounts] = useState<Map<string, number> | null>(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setParentId(defaultGroupParent(selectedRefs));
    setCounts(null);
    let cancelled = false;
    // Each ref's own variant count: a member with variants can't become one.
    void Promise.all(selectedRefs.map(async (r) => [r.id, await getVariantCountForParent(brand.id, r.id)] as const))
      .then((entries) => {
        if (!cancelled) setCounts(new Map(entries));
      })
      .catch((err) => {
        console.error('Could not count variants', err);
        toast.error('Could not check the selection for existing variants');
        if (!cancelled) setCounts(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedRefs, brand.id]);

  const plan = useMemo(
    () => (parentId && counts ? planGroupAsVariants(selectedRefs, parentId, counts) : null),
    [parentId, counts, selectedRefs],
  );
  const parentHasVariants = (parentId && counts?.get(parentId)) || 0;

  const handleApply = async () => {
    if (!plan || plan.conflicts.length > 0 || plan.moveIds.length === 0) return;
    setApplying(true);
    try {
      const moved = await applyGroupAsVariants(brand.id, plan, selectedRefs);
      void logAdminEvents([
        {
          name: 'variant_group_created',
          params: { brandId: brand.id, parentId: plan.parentId, memberCount: moved, source: 'manual' },
        },
      ]);
      toast.success(`Grouped ${moved} reference${moved === 1 ? '' : 's'} as variants`);
      onGrouped();
      onClose();
    } catch (err) {
      console.error('Group as variants failed', err);
      toast.error(err instanceof Error ? err.message : 'Grouping failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Group as variants of a parent" size="lg">
      <div className="space-y-3 text-sm">
        <p className="text-gray-600">Which should be the parent? The others become its variants.</p>
        {counts === null ? (
          <p className="text-gray-500">Checking the selection…</p>
        ) : (
          <ul className="max-h-[45vh] overflow-y-auto divide-y divide-gray-100 rounded border border-gray-200">
            {selectedRefs.map((r) => {
              const conflict = plan?.conflicts.find((c) => c.refId === r.id);
              const own = counts.get(r.id) ?? 0;
              return (
                <li key={r.id} className="flex items-start gap-2 p-2">
                  <input
                    type="radio"
                    name="group-parent"
                    aria-label={`Parent: ${r.reference}`}
                    checked={parentId === r.id}
                    onChange={() => setParentId(r.id)}
                    className="mt-1"
                  />
                  <div className="min-w-0">
                    <span className="font-mono text-gray-900">{r.reference}</span>
                    {r.model && <span className="text-gray-600"> · {r.model}</span>}
                    {own > 0 && <span className="ml-2 text-xs text-gray-500">{own} variant{own === 1 ? '' : 's'}</span>}
                    {conflict && <p className="text-xs text-red-700">{conflict.reason}</p>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {plan && plan.conflicts.length > 0 && (
          <p className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
            {plan.conflicts.length} conflict{plan.conflicts.length === 1 ? '' : 's'} — nothing will be written until they
            are resolved (pick another parent, or deselect / ungroup those references).
          </p>
        )}
        {plan && plan.alreadyGrouped.length > 0 && (
          <p className="text-xs text-gray-500">{plan.alreadyGrouped.length} already a variant of this parent — left as is.</p>
        )}
        {parentHasVariants > 0 && (
          <p className="text-xs text-gray-500">The parent already has {parentHasVariants} variant(s); these join them.</p>
        )}
        <p className="text-xs text-gray-500">
          No reference is re-created or re-keyed, so user watches, images and calibre links keep pointing at the same
          documents. Variants leave consumer search (the parent stays), and the parent&rsquo;s variant aggregates refresh
          on their own.
        </p>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => void handleApply()}
            loading={applying}
            disabled={!plan || plan.conflicts.length > 0 || plan.moveIds.length === 0 || applying}
          >
            Group {plan?.moveIds.length ?? 0} as variants
          </Button>
        </div>
      </div>
    </Modal>
  );
}
